import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { SliderModule } from 'primeng/slider';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { RadioButtonModule } from 'primeng/radiobutton';
import { InputTextModule } from 'primeng/inputtext';
import { RippleModule } from 'primeng/ripple';

import { FeatureGroupDto } from '../../../dto/feature.group.dto';
import { GroupResponseDto } from '../../../dto/group.response.dto';
import { AdInformationDto } from '../../../dto/ad.information.dto';
import { SearchCriteria, SortOption } from '../../../models/search-criteria.model';

interface FeatureItem {
    code: string;
    label: string;
}

/**
 * Panel de filtros — 100% presentacional.
 * Recibe las opciones por `@Input()` y emite cambios por `@Output()`.
 * NO inyecta services ni subjects: la orquestación vive en el facade.
 */
@Component({
    selector: 'app-filters',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        CheckboxModule,
        SliderModule,
        ButtonModule,
        DividerModule,
        RadioButtonModule,
        InputTextModule,
        RippleModule,
    ],
    templateUrl: './filters.component.html',
    styleUrls: ['./filters.component.scss'],
})
export class FiltersComponent {
    @Input() totalProperties = 0;
    @Input() primaryFeatures: FeatureGroupDto[] = [];
    @Input() secondaryFeatures: GroupResponseDto[] = [];
    @Input() offers: AdInformationDto[] = [];

    @Output() filtersChanged = new EventEmitter<Partial<SearchCriteria>>();
    @Output() clear = new EventEmitter<void>();

    /** Opciones de orden con las keys EXACTAS que acepta el backend. */
    readonly sortOptions: { label: string; value: SortOption }[] = [
        { label: 'Novedades primero', value: 'last_first' },
        { label: 'Precio: bajo a alto', value: 'price_min' },
        { label: 'Precio: alto a bajo', value: 'price_max' },
    ];

    readonly priceMin = 0;
    readonly priceMax = 100000;

    selectedSort: SortOption | null = null;
    selectedAdId: number | null = null;
    selectedFeatureCodes: string[] = [];
    priceRange: number[] = [this.priceMin, this.priceMax];
    featuresExpanded = false;

    get featureItems(): FeatureItem[] {
        const items: FeatureItem[] = [];
        for (const group of this.secondaryFeatures ?? []) {
            for (const feature of group.featureList ?? []) {
                items.push({ code: feature.featureCode, label: feature.featureLabel });
            }
        }
        return items;
    }

    get visibleFeatures(): FeatureItem[] {
        const items = this.featureItems;
        return this.featuresExpanded ? items : items.slice(0, 5);
    }

    emit(): void {
        const payload: Partial<SearchCriteria> = {
            orderOptions: this.selectedSort,
            adId: this.selectedAdId,
            featureFilters: this.selectedFeatureCodes,
            amount1: this.priceRange[0] !== this.priceMin ? String(this.priceRange[0]) : null,
            amount2: this.priceRange[1] !== this.priceMax ? String(this.priceRange[1]) : null,
        };
        this.filtersChanged.emit(payload);
    }

    clearFilters(): void {
        this.selectedSort = null;
        this.selectedAdId = null;
        this.selectedFeatureCodes = [];
        this.priceRange = [this.priceMin, this.priceMax];
        this.clear.emit();
    }

    hasActiveFilters(): boolean {
        return (
            this.selectedSort != null ||
            this.selectedAdId != null ||
            this.selectedFeatureCodes.length > 0 ||
            this.priceRange[0] !== this.priceMin ||
            this.priceRange[1] !== this.priceMax
        );
    }
}
